import React from 'react';
import { 
  RiOrganizationChart, 
  RiGlobalLine, 
  RiCalendarLine, 
  RiFocus3Line,
  RiShieldCheckLine,
  RiLockLine,
  RiDatabaseLine,
  RiShieldStarLine,
  RiCheckboxCircleFill,
  RiSmartphoneLine,
  RiEyeLine,
  RiSettings3Line
} from 'react-icons/ri';
import SEO from '../components/SEO';
import './About.css';

const About = () => {
  return (
    <>
      <SEO
        title="About NEIR - National Equipment Identity Register | BTRC Bangladesh Government"
        description="Learn about the National Equipment Identity Register (NEIR) system by Bangladesh Telecommunication Regulatory Commission (BTRC). Official government initiative for mobile device registration, IMEI verification, and telecommunications security in Bangladesh. Understand how NEIR works, its architecture, mission, and benefits for mobile device users in Bangladesh."
        keywords="About NEIR, About NEIR BTRC, About NEIR Bangladesh, About NEIR.gov.bd, About NEIR.btrc.gov.bd, About neir.btrc.gov.bd, National Equipment Identity Register, BTRC NEIR system, Bangladesh Telecommunication Regulatory Commission, government mobile database, NEIR architecture, mobile device registration system, IMEI verification system, Bangladesh government telecommunications, official mobile database, government mobile security, BTRC system architecture, NEIR mission, government mobile services, Bangladesh telecom regulatory commission, mobile device management, government telecommunications infrastructure, official device registration, Bangladesh mobile network security, government mobile database system, NEIR benefits, mobile device compliance, government telecommunications policy, Bangladesh mobile device verification, official government mobile services, BTRC official system, NEIR official information, government mobile registration process, Bangladesh telecommunications authority, official mobile device database, neir.btrc.gov.bd about, www.neir.btrc.gov.bd about, neir portal about, btrc neir portal about, neir btrc website about, neir btrc official about, neir btrc system about, neir btrc database about, neir btrc service about, neir btrc bangladesh about, neir btrc gov about, neir btrc government about, .gov.bd about, gov.bd domain about, government domain Bangladesh about, official government domain about, Bangladesh government website about, government portal Bangladesh about, official portal Bangladesh about, btrc.gov.bd about, neir.btrc.gov.bd about, www.btrc.gov.bd about, www.neir.btrc.gov.bd about"
        ogTitle="About NEIR - National Equipment Identity Register | BTRC Bangladesh"
        ogDescription="Learn about the National Equipment Identity Register (NEIR) system by Bangladesh Telecommunication Regulatory Commission (BTRC)."
        ogImage="/images/btrc-logo.png"
        canonicalUrl="https://neir.btrc.gov.bd/about"
      />
      <div className="about-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <div className="page-header-content">
            <div className="header-icon-wrapper">
              <RiShieldStarLine className="header-icon" />
            </div>
            <h1 className="page-title">
              <span className="gradient-text">About NEIR</span>
            </h1>
            <p className="page-subtitle">
              National Equipment Identity Register - Protecting mobile device security and preventing criminal activities in Bangladesh
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section section">
        <div className="container">
          <div className="about-content-grid">
            <div className="about-text">
              <h2 className="section-title">
                <span className="gradient-text">About NEIR</span>
              </h2>
              <p className="lead">
                The National Equipment Identity Register (NEIR) has been deployed by the 
                Bangladesh Telecommunications Regulatory Commission (BTRC) to ensure the 
                accountability of all mobile handsets used within Bangladesh.
              </p>
              <p>
                Mobile phone usage has significantly increased over the last decade, bringing 
                both positive impacts and a rise in criminal activities such as the use of 
                stolen or counterfeit mobile devices, extortion, intimidation, and harassment.
              </p>
              <p>
                NEIR is a central platform designed to control the access of mobile devices 
                into the mobile network through necessary device verification. The system 
                maintains data such as International Mobile Equipment Identity (IMEI), Mobile 
                Station Integrated Services Digital Network (MSISDN), and User's National ID (NID) 
                for all mobile devices.
              </p>
              <p>
                It connects with the Equipment Identity Registers (EIRs) of all Cellular Mobile 
                Phone Operators (CMPO) and other related stakeholders. In addition to maintaining 
                proper access control for all mobile devices in Bangladesh, NEIR also monitors 
                CMPO EIRs.
              </p>
              <div className="about-features">
                <div className="feature-tag">
                  <RiShieldCheckLine />
                  <span>Device Authentication</span>
                </div>
                <div className="feature-tag">
                  <RiLockLine />
                  <span>Security & Protection</span>
                </div>
                <div className="feature-tag">
                  <RiDatabaseLine />
                  <span>Central Database</span>
                </div>
                <div className="feature-tag">
                  <RiEyeLine />
                  <span>Real-time Monitoring</span>
                </div>
              </div>
            </div>
            <div className="about-cards">
              <div className="info-card card-1">
                <RiOrganizationChart className="card-icon" />
                <h5>Organization</h5>
                <p>Bangladesh Telecommunication Regulatory Commission (BTRC)</p>
              </div>
              <div className="info-card card-2">
                <RiGlobalLine className="card-icon" />
                <h5>Country</h5>
                <p>Bangladesh</p>
              </div>
              <div className="info-card card-3">
                <RiCalendarLine className="card-icon" />
                <h5>Project Duration</h5>
                <p>18 February 2020 to 1 July 2021</p>
              </div>
              <div className="info-card card-4">
                <RiFocus3Line className="card-icon" />
                <h5>Primary Goals</h5>
                <p>Accountability, Authentication, Crime Prevention</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* System Architecture Section */}
      <section className="architecture-section section">
        <div className="container">
          <div className="section-header text-center">
            <h2 className="section-title">
              <span className="gradient-text">NEIR System Architecture</span>
            </h2>
            <p className="section-subtitle">
              Understanding how NEIR integrates with Mobile Network Operators and regulatory systems
            </p>
          </div>
          <div className="architecture-diagram">
            <div className="diagram-container">
              <img 
                src="/images/architecture-diagram.png" 
                alt="NEIR System Architecture Flowchart" 
                className="architecture-img"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="diagram-fallback" style={{display: 'none'}}>
                <div className="architecture-description">
                  <h3>NEIR System Flow</h3>
                  <div className="flow-steps">
                    <div className="flow-step">
                      <h4>1. Mobile Network Operators (MNOs)</h4>
                      <p>Grameenphone, Banglalink, Robi, and Teletalk maintain their Equipment Identity Registers (EIRs)</p>
                    </div>
                    <div className="flow-step">
                      <h4>2. Data Collection</h4>
                      <p>EIRs provide IMEI, IMSI, and MSISDN data to the central NEIR system</p>
                    </div>
                    <div className="flow-step">
                      <h4>3. Database Integration</h4>
                      <p>NEIR connects with GSMA, NAID, and CBVMP databases for verification</p>
                    </div>
                    <div className="flow-step">
                      <h4>4. Device Classification</h4>
                      <p>Devices are categorized into White List, Gray List, Black List, and Roamers List</p>
                    </div>
                    <div className="flow-step">
                      <h4>5. Regulatory Oversight</h4>
                      <p>BTRC receives comprehensive data for regulatory functions and monitoring</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section section">
        <div className="container">
          <div className="section-header text-center">
            <h2 className="section-title">
              <span className="gradient-text">How NEIR Works</span>
            </h2>
            <p className="section-subtitle">
              Understanding the NEIR verification and monitoring process
            </p>
          </div>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-icon-wrapper">
                <RiSmartphoneLine className="step-icon" />
              </div>
              <h3 className="step-title">Device Registration</h3>
              <p className="step-description">
                Mobile devices are registered in the NEIR database with IMEI, MSISDN, and NID information 
                through network operators.
              </p>
            </div>

            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-icon-wrapper">
                <RiDatabaseLine className="step-icon" />
              </div>
              <h3 className="step-title">Database Verification</h3>
              <p className="step-description">
                NEIR connects with GSMA, NAID, and CBVMP databases to verify device authenticity and 
                legal status.
              </p>
            </div>

            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-icon-wrapper">
                <RiShieldCheckLine className="step-icon" />
              </div>
              <h3 className="step-title">Device Classification</h3>
              <p className="step-description">
                Devices are categorized into White List (legal), Gray List (under review), Black List 
                (blocked), and Roamers List.
              </p>
            </div>

            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-icon-wrapper">
                <RiSettings3Line className="step-icon" />
              </div>
              <h3 className="step-title">Network Control</h3>
              <p className="step-description">
                Network operators receive instructions to allow or block devices based on NEIR classification, 
                ensuring only legal devices access the network.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section section">
        <div className="container">
          <div className="mission-content">
            <div className="mission-icon-wrapper">
              <RiFocus3Line className="mission-icon" />
            </div>
            <h2 className="section-title">
              <span className="gradient-text-white">Our Mission</span>
            </h2>
            <p className="mission-text">
              To ensure the accountability of all mobile handsets used within Bangladesh and to provide 
              user authentication. The system aims to prevent criminal activities involving mobile handsets 
              and to offer better citizen services through analytical information generated by the system.
            </p>
            <div className="mission-goals">
              <div className="goal-item">
                <RiCheckboxCircleFill className="goal-icon" />
                <span>Prevent Criminal Activities</span>
              </div>
              <div className="goal-item">
                <RiCheckboxCircleFill className="goal-icon" />
                <span>Enhance National Security</span>
              </div>
              <div className="goal-item">
                <RiCheckboxCircleFill className="goal-icon" />
                <span>Protect Citizens</span>
              </div>
              <div className="goal-item">
                <RiCheckboxCircleFill className="goal-icon" />
                <span>Support Local Industry</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default About;

